import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { UIStore, Notification } from '../types/interfaces';
import {
  getNextScreen,
  getPreviousScreen,
  getFlowProgress,
  isConditionalScreen,
  type FlowType
} from '../config/flowDefinitions';

interface UIState {
  language: 'FR' | 'EN';
  theme: 'light' | 'dark';
  sidebarOpen: boolean;
  notifications: Notification[];
  // Navigation state
  currentFlow: FlowType | null;
  currentScreen: string;
  completedScreens: string[];
  skipConditions: Record<string, boolean>;
  // Eligibility state (for non-blocking API flow)
  eligibilityLoading: boolean;
  eligibilityRetrying: boolean;
  eligibilityRetryCount: number;
  eligibilityError: string | null;
  // Maintenance mode (DEV-6343)
  maintenance: boolean;
  maintenancePollIntervalMs: number;
}

const initialState: UIState = {
  language: 'FR', // Default to French for Canadian audience
  theme: 'light',
  sidebarOpen: false,
  notifications: [],
  // Navigation initial state
  currentFlow: null,
  currentScreen: '00-00-ACCUEIL',
  completedScreens: [],
  skipConditions: {},
  // Eligibility initial state
  eligibilityLoading: false,
  eligibilityRetrying: false,
  eligibilityRetryCount: 0,
  eligibilityError: null,
  // Maintenance initial state (DEV-6343). Default poll cadence overridden by the backend probe.
  maintenance: false,
  maintenancePollIntervalMs: 300000
};

export const useUIStore = create<UIStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // Set language preference
        setLanguage: (language: 'FR' | 'EN') => {
          set({ language }, false, 'setLanguage');

          // Update i18n language and cache
          import('../services/i18n').then(({ default: i18n }) => {
            const lang = language.toLowerCase();
            i18n.changeLanguage(lang).then(() => {
              // Force cache update in localStorage
              if (typeof window !== 'undefined') {
                localStorage.setItem('i18nextLng', lang);
              }
            });
          });
        },

        // Set theme preference
        setTheme: (theme: 'light' | 'dark') => {
          set({ theme }, false, 'setTheme');
          
          // Update document class for styling
          if (typeof document !== 'undefined') {
            document.documentElement.classList.toggle('dark', theme === 'dark');
          }
        },

        // Toggle sidebar visibility
        toggleSidebar: () => {
          set(state => ({ sidebarOpen: !state.sidebarOpen }), false, 'toggleSidebar');
        },

        // Add a new notification
        addNotification: (notification: Omit<Notification, 'id'>) => {
          const id = crypto.randomUUID();
          const newNotification: Notification = {
            ...notification,
            id,
            duration: notification.duration || 5000,
            persistent: notification.persistent || false
          };

          set(state => ({
            notifications: [...state.notifications, newNotification]
          }), false, 'addNotification');

          // Auto-remove non-persistent notifications
          if (!newNotification.persistent && newNotification.duration > 0) {
            setTimeout(() => {
              get().removeNotification(id);
            }, newNotification.duration);
          }
        },

        // Remove a specific notification
        removeNotification: (id: string) => {
          set(state => ({
            notifications: state.notifications.filter(n => n.id !== id)
          }), false, 'removeNotification');
        },

        // Clear all notifications
        clearNotifications: () => {
          set({ notifications: [] }, false, 'clearNotifications');
        },

        // Show success notification
        showSuccess: (message: string, title?: string) => {
          get().addNotification({
            type: 'success',
            message,
            title,
            duration: 5000
          });
        },

        // Show error notification
        showError: (message: string, title?: string) => {
          get().addNotification({
            type: 'error',
            message,
            title,
            persistent: true // Errors should be manually dismissed
          });
        },

        // Show warning notification
        showWarning: (message: string, title?: string) => {
          get().addNotification({
            type: 'warning',
            message,
            title,
            duration: 7000
          });
        },

        // Show info notification
        showInfo: (message: string, title?: string) => {
          get().addNotification({
            type: 'info',
            message,
            title,
            duration: 5000
          });
        },

        // Navigation methods

        // Set the current flow (Medical, Cancellation, or Baggage)
        // When changing flows, clear completed screens to avoid mixing screens from different flows
        setFlow: (flow: FlowType) => {
          set({
            currentFlow: flow,
            completedScreens: ['02-00-GARANTIE'] // Keep only the common claim type selection screen
          }, false, 'setFlow');
        },

        // Set the current screen
        setCurrentScreen: (screenCode: string) => {
          set({ currentScreen: screenCode }, false, 'setCurrentScreen');
        },

        // Mark a screen as completed
        completeScreen: (screenCode: string) => {
          set(state => ({
            completedScreens: state.completedScreens.includes(screenCode)
              ? state.completedScreens
              : [...state.completedScreens, screenCode]
          }), false, 'completeScreen');
        },

        // Get the next screen based on current flow and conditions
        getNextScreenCode: (): string | null => {
          const state = get();
          if (!state.currentFlow) return null;

          return getNextScreen(
            state.currentScreen,
            state.currentFlow,
            state.skipConditions
          );
        },

        // Get the previous screen based on current flow
        getPreviousScreenCode: (): string | null => {
          const state = get();
          if (!state.currentFlow) return null;

          return getPreviousScreen(state.currentScreen, state.currentFlow);
        },

        // Check if a screen should be skipped
        shouldSkipScreen: (screenCode: string): boolean => {
          const state = get();
          if (!state.currentFlow) return false;

          if (!isConditionalScreen(screenCode, state.currentFlow)) {
            return false;
          }

          // Check skip conditions
          // This will be enhanced based on actual business logic
          return false;
        },

        // Set skip conditions for conditional screens
        setSkipConditions: (conditions: Record<string, boolean>) => {
          set(state => ({
            skipConditions: { ...state.skipConditions, ...conditions }
          }), false, 'setSkipConditions');
        },

        // Get current flow progress
        getFlowProgressPercentage: (): number => {
          const state = get();
          if (!state.currentFlow) return 0;

          return getFlowProgress(state.currentScreen, state.currentFlow);
        },

        // Reset navigation state
        resetNavigation: () => {
          set({
            currentFlow: null,
            currentScreen: '00-00-ACCUEIL',
            completedScreens: [],
            skipConditions: {}
          }, false, 'resetNavigation');
        },

        // Eligibility methods
        setEligibilityLoading: (loading: boolean) => {
          set({ eligibilityLoading: loading }, false, 'setEligibilityLoading');
        },

        setEligibilityRetrying: (retrying: boolean, retryCount?: number) => {
          set({
            eligibilityRetrying: retrying,
            eligibilityRetryCount: retryCount !== undefined ? retryCount : get().eligibilityRetryCount
          }, false, 'setEligibilityRetrying');
        },

        setEligibilityError: (error: string | null) => {
          set({ eligibilityError: error }, false, 'setEligibilityError');
        },

        resetEligibilityState: () => {
          set({
            eligibilityLoading: false,
            eligibilityRetrying: false,
            eligibilityRetryCount: 0,
            eligibilityError: null
          }, false, 'resetEligibilityState');
        },

        // Maintenance mode (DEV-6343). Transient (not persisted): on reload the bootstrap probe re-checks.
        setMaintenance: (active: boolean, pollIntervalMs?: number) => {
          set(
            (state) => ({
              maintenance: active,
              maintenancePollIntervalMs: pollIntervalMs ?? state.maintenancePollIntervalMs
            }),
            false,
            'setMaintenance'
          );
        }
      }),
      {
        name: 'ui-store',
        partialize: (state) => ({
          language: state.language,
          theme: state.theme,
          currentFlow: state.currentFlow,
          currentScreen: state.currentScreen,
          completedScreens: state.completedScreens,
          skipConditions: state.skipConditions
          // Don't persist notifications or sidebar state
        }),
        version: 2 // Increment version for new navigation state
      }
    ),
    { name: 'ui-store' }
  )
);